import React, { useState } from 'react';
import api, { getApiErrorMessage } from '../api';
import CopyButton from './CopyButton';

const PROVIDERS = [
    ['none', 'Не выгружать'],
    ['yadisk', 'Яндекс Диск'],
    ['s3', 'Объектное хранилище S3'],
];

function formatDate(value) {
    if (!value) return '—';
    const d = new Date(value);
    return Number.isNaN(d.getTime()) ? '—' : d.toLocaleString('ru-RU', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' });
}

function formFrom(cloud) {
    return {
        provider: cloud.provider,
        yadisk_token: '',
        yadisk_folder: cloud.yadisk_folder || 'app:/svod',
        s3_endpoint: cloud.s3_endpoint || '',
        s3_region: cloud.s3_region || '',
        s3_bucket: cloud.s3_bucket || '',
        s3_access_key: cloud.s3_access_key || '',
        s3_secret_key: '',
        s3_prefix: cloud.s3_prefix || 'svod',
    };
}

// Облако для резервных копий: Яндекс Диск или S3-совместимое хранилище, шифрование, проверка.
function AdminBackupCloud({ cloud, onChanged }) {
    const [form, setForm] = useState(() => formFrom(cloud));
    const [busy, setBusy] = useState(false);
    const [notice, setNotice] = useState(null);
    const [test, setTest] = useState(null);
    const [clientId, setClientId] = useState('');
    const [password, setPassword] = useState(null);

    const set = (key, value) => { setForm((f) => ({ ...f, [key]: value })); setNotice(null); setTest(null); };
    const payload = () => {
        const p = { ...form };
        if (!p.yadisk_token.trim()) delete p.yadisk_token;
        if (!p.s3_secret_key.trim()) delete p.s3_secret_key;
        return p;
    };

    const save = async () => {
        setBusy(true);
        try {
            const res = await api.put('/admin/backups/offsite', payload());
            onChanged(res.data);
            setForm(formFrom(res.data.offsite));
            setNotice({ type: 'success', text: form.provider === 'none' ? 'Выгрузка в облако выключена.' : 'Сохранено. Копии начнут выгружаться в течение 5 минут.' });
        } catch (err) {
            setNotice({ type: 'error', text: getApiErrorMessage(err, 'Не удалось сохранить') });
        } finally {
            setBusy(false);
        }
    };

    const check = async () => {
        setBusy(true);
        setTest(null);
        try {
            const res = await api.post('/admin/backups/offsite/test', payload(), { timeout: 120000 });
            setTest(res.data);
        } catch (err) {
            setTest({ ok: false, error: getApiErrorMessage(err, 'Ошибка проверки') });
        } finally {
            setBusy(false);
        }
    };

    const action = async (path, text) => {
        try {
            await api.post(`/admin/backups/offsite/${path}`);
            setNotice({ type: 'success', text });
        } catch (err) {
            setNotice({ type: 'error', text: getApiErrorMessage(err, 'Не получилось') });
        }
    };

    const reveal = async () => {
        try {
            const res = await api.get('/admin/backups/offsite/password');
            setPassword(res.data.password);
        } catch (err) {
            setNotice({ type: 'error', text: getApiErrorMessage(err, 'Не удалось получить пароль') });
        }
    };

    const applyPreset = (key) => {
        const preset = cloud.presets?.[key];
        if (preset) setForm((f) => ({ ...f, s3_endpoint: preset.endpoint, s3_region: preset.region }));
    };

    const verify = cloud.verify;
    const authLink = clientId.trim()
        ? `https://oauth.yandex.ru/authorize?response_type=token&client_id=${encodeURIComponent(clientId.trim())}`
        : null;

    return (
        <div className="nl-block bkp-cloud">
            <h3 className="nl-block__title">Облако</h3>
            <p className="nl-hint">
                Копии и логи автоматически уходят в облако: ежедневные — сразу после создания, почасовые и логи —
                в течение часа. Перед отправкой каждый файл шифруется, поэтому облачный сервис не видит данные.
                Раз в неделю СВОД сам скачивает последнюю копию из облака и проверяет, что она восстанавливается.
            </p>

            {cloud.configured && (
                <div className={`srv-alert ${cloud.last_error ? 'srv-alert--warning' : 'bkp-ok'}`}>
                    {cloud.last_upload_at ? <>Последняя выгрузка: <strong>{formatDate(cloud.last_upload_at)}</strong>.</> : 'Выгрузок ещё не было.'}
                    {cloud.pending > 0 && <> Ждут выгрузки: {cloud.pending}.</>}
                    {cloud.last_error && <div className="nl-error-text">Ошибка: {cloud.last_error}</div>}
                    {verify && (
                        <div>
                            Проверка восстановления {formatDate(verify.at)}:{' '}
                            {verify.ok ? <b>копия {verify.filename} скачана, расшифрована и читается</b> : <b className="srv-bad">{verify.error}</b>}
                        </div>
                    )}
                </div>
            )}

            <div className="form-group">
                <label htmlFor="bkp-provider">Куда выгружать</label>
                <div className="nl-seg" role="radiogroup" id="bkp-provider" aria-label="Куда выгружать">
                    {PROVIDERS.map(([v, label]) => (
                        <button key={v} type="button" role="radio" aria-checked={form.provider === v}
                                className={form.provider === v ? 'is-active' : ''} onClick={() => set('provider', v)}>
                            {label}
                        </button>
                    ))}
                </div>
            </div>

            {form.provider === 'yadisk' && (
                <>
                    <details className="bkp-howto" open={!cloud.yadisk_token_masked}>
                        <summary>Как получить токен Яндекс Диска (5 минут)</summary>
                        <ol>
                            <li>Откройте <a href="https://oauth.yandex.ru/client/new" target="_blank" rel="noreferrer">oauth.yandex.ru/client/new</a> под аккаунтом, на чей Диск будут уходить копии.</li>
                            <li>Название — «СВОД копии». Платформа — «Веб-сервисы», Redirect URI — <code>https://oauth.yandex.ru/verification_code</code>.</li>
                            <li>Доступы: «Яндекс Диск REST API» → <b>«Доступ к папке приложения на Диске»</b> и <b>«Доступ к информации о Диске»</b>. Создайте приложение.</li>
                            <li>Скопируйте ClientID и вставьте сюда:{' '}
                                <input type="text" className="bkp-inline-input" placeholder="ClientID" value={clientId}
                                       onChange={(e) => setClientId(e.target.value)} aria-label="ClientID" />
                                {authLink && <> → <a href={authLink} target="_blank" rel="noreferrer">получить токен</a></>}
                            </li>
                            <li>Разрешите доступ, скопируйте показанный токен и вставьте в поле ниже. Копии будут в папке «Приложения → СВОД копии» на Диске.</li>
                        </ol>
                        <p className="nl-hint nl-hint--small">Токен действует около года. Когда истечёт, выгрузка остановится с ошибкой — получите новый так же.</p>
                    </details>
                    <div className="form-group">
                        <label htmlFor="bkp-yatoken">OAuth-токен</label>
                        <input id="bkp-yatoken" type="password" autoComplete="new-password" value={form.yadisk_token}
                               placeholder={cloud.yadisk_token_masked ? `Сохранён: ${cloud.yadisk_token_masked}. Оставьте пустым, чтобы не менять` : 'y0_…'}
                               onChange={(e) => set('yadisk_token', e.target.value)} />
                    </div>
                </>
            )}

            {form.provider === 's3' && (
                <>
                    <p className="nl-hint nl-hint--small">
                        Подходит любое S3-совместимое хранилище. Создайте бакет (приватный) и сервисный ключ доступа с правом
                        записи в него. Надёжнее хранилища в другом дата-центре, чем ваш сервер.
                    </p>
                    <div className="form-group">
                        <label htmlFor="bkp-preset">Провайдер</label>
                        <select id="bkp-preset" defaultValue="" onChange={(e) => applyPreset(e.target.value)}>
                            <option value="">Выберите, чтобы подставить адрес…</option>
                            {Object.entries(cloud.presets || {}).map(([k, p]) => <option key={k} value={k}>{p.label}</option>)}
                        </select>
                    </div>
                    <div className="mai-grid mai-grid--even">
                        <div className="form-group">
                            <label htmlFor="bkp-endpoint">Адрес (endpoint)</label>
                            <input id="bkp-endpoint" type="url" placeholder="https://storage.yandexcloud.net" value={form.s3_endpoint}
                                   onChange={(e) => set('s3_endpoint', e.target.value)} />
                        </div>
                        <div className="form-group">
                            <label htmlFor="bkp-region">Регион</label>
                            <input id="bkp-region" type="text" placeholder="ru-central1" value={form.s3_region}
                                   onChange={(e) => set('s3_region', e.target.value)} />
                        </div>
                        <div className="form-group">
                            <label htmlFor="bkp-bucket">Бакет</label>
                            <input id="bkp-bucket" type="text" placeholder="svod-backups" value={form.s3_bucket}
                                   onChange={(e) => set('s3_bucket', e.target.value)} />
                        </div>
                        <div className="form-group">
                            <label htmlFor="bkp-prefix">Папка в бакете</label>
                            <input id="bkp-prefix" type="text" placeholder="svod" value={form.s3_prefix}
                                   onChange={(e) => set('s3_prefix', e.target.value)} />
                        </div>
                        <div className="form-group">
                            <label htmlFor="bkp-ak">Идентификатор ключа (Access key)</label>
                            <input id="bkp-ak" type="text" value={form.s3_access_key} onChange={(e) => set('s3_access_key', e.target.value)} />
                        </div>
                        <div className="form-group">
                            <label htmlFor="bkp-sk">Секретный ключ (Secret key)</label>
                            <input id="bkp-sk" type="password" autoComplete="new-password" value={form.s3_secret_key}
                                   placeholder={cloud.s3_secret_key_masked ? `Сохранён: ${cloud.s3_secret_key_masked}` : ''}
                                   onChange={(e) => set('s3_secret_key', e.target.value)} />
                        </div>
                    </div>
                </>
            )}

            {test && (
                <div className={`mai-alert ${test.ok ? 'mai-alert--success' : 'mai-alert--error'}`} role="status">
                    {test.ok
                        ? <>Подключение работает: пробный файл записан и удалён.{test.total ? ` Свободно на Диске: ${((test.total - test.used) / 1024 ** 3).toFixed(1)} ГБ.` : ''}</>
                        : <>Не работает: {test.error}</>}
                </div>
            )}
            {notice && <div className={`admin-notice admin-notice--${notice.type}`} role="status">{notice.text}</div>}

            <div className="nl-actions">
                {form.provider !== 'none' && (
                    <button type="button" className="btn btn--secondary" onClick={check} disabled={busy}>Проверить подключение</button>
                )}
                <button type="button" className="btn btn--success" onClick={save} disabled={busy}>Сохранить</button>
                {cloud.configured && (
                    <>
                        <button type="button" className="btn btn--secondary" onClick={() => action('upload', 'Выгрузка запущена — обновите список через минуту.')}>Выгрузить сейчас</button>
                        <button type="button" className="btn btn--secondary" onClick={() => action('verify', 'Проверка запущена — результат появится здесь через минуту.')}>Проверить восстановление</button>
                    </>
                )}
            </div>

            {form.provider !== 'none' && (
                <div className="bkp-password">
                    <div className="bkp-cmd__title">Пароль шифрования</div>
                    <p className="nl-hint nl-hint--small">
                        Без этого пароля копии из облака не расшифровать. Сохраните его в надёжном месте вне сервера,
                        например в менеджере паролей: если сервер пропадёт, пароль понадобится для восстановления.
                    </p>
                    {password ? (
                        <div className="mai-row">
                            <input type="text" readOnly value={password} onFocus={(e) => e.target.select()} aria-label="Пароль шифрования" />
                            <CopyButton text={password} className="btn btn--secondary btn--sm" />
                        </div>
                    ) : (
                        <button type="button" className="btn btn--secondary btn--sm" onClick={reveal}>Показать пароль</button>
                    )}
                </div>
            )}
        </div>
    );
}

export default AdminBackupCloud;
